import type { ReactNode } from "react";
import type { Accept, FileRejection } from "react-dropzone";
import { Button, FormFeedback } from "reactstrap";
import Dropzone from "react-dropzone";
import { toast } from "react-toastify";
import { formatFileSize } from "utils/fileUtils";

/** Common enquiry-site-visit / document style: PNG, JPG, JPEG, and PDF only. */
export const DEFAULT_IMAGES_AND_PDF_ACCEPT: Accept = {
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  "application/pdf": [".pdf"],
};

export const DEFAULT_MAX_FILE_BYTES = 15 * 1024 * 1024;

export type FileDropzoneProps = {
  onDropAccepted: (files: File[]) => void;
  /** MIME/extension map for react-dropzone. Omit to allow any file type. */
  accept?: Accept;
  /** Per-file max size in bytes. Omit for no limit. */
  maxSize?: number;
  multiple?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  errorText?: ReactNode;
  idleTitle?: string;
  activeTitle?: string;
  /** Helper text below the title (e.g. types and size limits). */
  description?: ReactNode;
  browseLabel?: string;
  /** Shown when a file fails the `accept` filter. */
  invalidTypeMessage?: string;
  /**
   * When files are rejected and `onDropRejected` is not set, show a toast
   * using `invalidTypeMessage` and max size (if `maxSize` is set).
   */
  notifyOnReject?: boolean;
  /** Full control over rejections; when set, default toast is skipped. */
  onDropRejected?: (rejections: FileRejection[]) => void;
  /** Extra classes on the clickable drop area (state classes are still applied). */
  className?: string;
  /** Reduces padding and hides the upload icon for use in space-constrained layouts. */
  compact?: boolean;
};

const defaultIdleTitle = "Drag files here or browse";
const defaultActiveTitle = "Drop files to add them";
const defaultBrowseLabel = "Browse files";
const defaultInvalidType = "This file type is not allowed.";

function defaultRejectToast(
  rejections: FileRejection[],
  maxSize: number | undefined,
  invalidTypeMessage: string,
) {
  const first = rejections[0];
  if (!first) return;
  const parts = first.errors.map((e) => {
    if (e.code === "file-too-large" && maxSize != null)
      return `Max size is ${formatFileSize(maxSize)} per file`;
    if (e.code === "file-invalid-type") return invalidTypeMessage;
    return e.message;
  });
  toast.error(parts.join(". ") || "Could not add file");
}

export function FileDropzone({
  onDropAccepted,
  accept,
  maxSize,
  multiple = true,
  disabled = false,
  invalid = false,
  errorText,
  idleTitle = defaultIdleTitle,
  activeTitle = defaultActiveTitle,
  description,
  browseLabel = defaultBrowseLabel,
  invalidTypeMessage = defaultInvalidType,
  notifyOnReject = true,
  onDropRejected,
  className = "",
  compact = false,
}: FileDropzoneProps) {
  return (
    <>
      <Dropzone
        accept={accept}
        multiple={multiple}
        maxSize={maxSize}
        disabled={disabled}
        onDrop={(acceptedFiles) => {
          if (acceptedFiles.length > 0) onDropAccepted(acceptedFiles);
        }}
        onDropRejected={(rejections) => {
          if (onDropRejected) {
            onDropRejected(rejections);
            return;
          }
          if (notifyOnReject) {
            defaultRejectToast(rejections, maxSize, invalidTypeMessage);
          }
        }}
      >
        {({ getRootProps, getInputProps, isDragActive }) => (
          <div
            {...getRootProps()}
            className={`rounded-3 border border-2 text-center cursor-pointer user-select-none ${compact ? "px-3 py-3" : "px-3 py-4 py-md-5"} ${className} ${
              disabled ? "opacity-50 pe-none" : ""
            } ${
              invalid
                ? "border-danger bg-danger-subtle"
                : isDragActive
                  ? "border-primary bg-primary-subtle"
                  : "border-dashed bg-light bg-opacity-50"
            }`.trim()}
          >
            <input {...getInputProps()} />
            {!compact ? (
              <div className="avatar-md mx-auto mb-3">
                <span className="avatar-title rounded-circle bg-primary-subtle text-primary fs-20">
                  <i
                    className={
                      isDragActive
                        ? "ri-download-cloud-2-line"
                        : "ri-upload-cloud-2-line"
                    }
                  />
                </span>
              </div>
            ) : (
              <i
                className={`fs-20 mb-1 text-primary d-block ${isDragActive ? "ri-download-cloud-2-line" : "ri-upload-cloud-2-line"}`}
              />
            )}
            <h6 className={`mb-1 ${compact ? "fs-13" : "mb-2"}`}>
              {isDragActive ? activeTitle : idleTitle}
            </h6>
            {description != null ? (
              <div
                className={`text-muted small px-sm-4 mx-auto ${compact ? "mb-2" : "mb-3 mb-md-4"}`}
                style={{ maxWidth: "420px" }}
              >
                {description}
              </div>
            ) : null}
            <Button
              type="button"
              color="primary"
              outline
              size="sm"
              className="px-3"
            >
              <i className="ri-folder-open-line me-1" />
              {browseLabel}
            </Button>
          </div>
        )}
      </Dropzone>
      {invalid && errorText ? (
        <FormFeedback type="invalid" className="d-block mt-2">
          {errorText}
        </FormFeedback>
      ) : null}
    </>
  );
}
